import type { Task } from "@app/core";
import { Button, Calendar, Card, MapPin, Repeat } from "@app/ui";

import { formatDuration } from "../../lib/format.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import type { NextUp } from "./agenda.js";
import { useTaskAccent } from "./useTaskAccent.js";

interface Props {
  pick: NextUp;
  now: Date;
  onEdit: (task: Task) => void;
  onView: (task: Task) => void;
}

/**
 * The Today spotlight: the item happening right now (pulsing live dot,
 * "until hh:mm") or the next timed one ahead ("in 1 h 20 min"), washed in the
 * task's own color. Click opens the editor; the calendar button jumps to it.
 */
export function NextUpCard({ pick, now, onEdit, onView }: Props) {
  const { t: tr } = useI18n();
  const tf = useTimeFormat();
  const accent = useTaskAccent(pick.task);
  const live = pick.kind === "now";

  const minutesUntil = Math.max(1, Math.round((pick.start.getTime() - now.getTime()) / 60_000));
  const hasEnd = pick.end.getTime() > pick.start.getTime();
  const range = hasEnd
    ? tf.range(
        pick.start.getHours() * 60 + pick.start.getMinutes(),
        (pick.end.getHours() * 60 + pick.end.getMinutes()) % 1440,
      )
    : tf.hmDate(pick.start);

  return (
    <Card
      className="relative overflow-hidden p-4 transition-shadow hover:shadow-md"
    >
      <span aria-hidden className="absolute inset-y-0 left-0 w-1" style={{ background: accent }} />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: `linear-gradient(100deg, ${accent}2b, ${accent}09 45%, transparent 75%)` }}
      />

      <div className="relative flex items-center gap-3 pl-1.5">
        <div
          role="button"
          tabIndex={0}
          onClick={() => onEdit(pick.task)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            onEdit(pick.task);
          }}
          title={tr("agenda.clickToEdit")}
          className="min-w-0 flex-1 cursor-pointer rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[calc(11px*var(--app-text-scale))] font-semibold uppercase tracking-wider" style={{ color: accent }}>
            {live && <PulseDot accent={accent} />}
            {live ? tr("today.happeningNow") : tr("today.upNext")}
            <span className="font-medium normal-case tracking-normal text-muted-foreground">
              {live
                ? tr("today.until", { time: tf.hmDate(pick.end) })
                : tr("today.startsIn", { duration: formatDuration(minutesUntil) })}
            </span>
          </div>
          <p className="mt-0.5 flex min-w-0 items-start gap-1.5 text-lg font-semibold leading-snug">
            <span className="min-w-0 break-words sm:truncate">
              {pick.task.title || <em className="font-normal text-muted-foreground">{tr("agenda.untitled")}</em>}
            </span>
            {pick.task.recurrence_id && <Repeat className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-label={tr("agenda.recurring")} />}
          </p>
          <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            <span className="tabular-nums">{range}</span>
            {pick.task.location && (
              <span className="flex min-w-0 items-center gap-0.5">
                <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">{pick.task.location}</span>
              </span>
            )}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-foreground"
          title={tr("agenda.showOnCalendar")}
          aria-label={tr("agenda.showOnCalendar")}
          onClick={(e) => {
            e.stopPropagation();
            onView(pick.task);
          }}
        >
          <Calendar className="h-4 w-4" />
        </Button>
      </div>
    </Card>
  );
}

/** Soft pulse marking the live pick — CSS-driven (`.live-pulse`), so it never
 *  holds a JS animation loop open; reduced motion leaves the static dot. */
function PulseDot({ accent }: { accent: string }) {
  return (
    <span className="relative grid h-2.5 w-2.5 place-items-center" aria-hidden>
      <span className="live-pulse absolute h-full w-full rounded-full" style={{ background: accent }} />
      <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
    </span>
  );
}
